import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "../apiConfig";
import { useAuth } from "../context/AuthContext";

export default function CoreSystems() {
  const navigate = useNavigate();
  const { user, initialized } = useAuth();

  const isInternalUser = user?.role === "ADMIN" || user?.role === "STAFF";

  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessMap, setAccessMap] = useState({});

  useEffect(() => {
    if (!initialized) return;

    loadSystems();
  }, [initialized, isInternalUser]);

  async function loadSystems() {
    try {
      setLoading(true);

      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const headers = {
        Authorization: `Bearer ${session?.token}`,
      };

      const res = await fetch(`${API_URL}/api/systems/list-with-progress`, {
        headers,
      });

      const json = await res.json();

      if (json.ok) {
        const systemsData = json.systems || [];
        setSystems(systemsData);

        if (isInternalUser) {
          const map = {};
          systemsData.forEach((system) => {
            map[system.id] = true;
          });
          setAccessMap(map);
        } else {
          await loadAccess(systemsData);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadAccess(systemsList) {
    try {
      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const results = await Promise.all(
        systemsList.map(async (system) => {
          const res = await fetch(`${API_URL}/api/access/check`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session?.token}`,
            },
            body: JSON.stringify({
              type: "system",
              resourceId: system.id,
            }),
          });

          const json = await res.json();

          return {
            id: system.id,
            access: json.access,
          };
        })
      );

      const map = {};
      results.forEach((r) => {
        map[r.id] = r.access;
      });

      setAccessMap(map);
    } catch (err) {
      console.error(err);
    }
  }

  if (loading || !initialized) {
    return <div style={{ padding: 40 }}>Cargando sistemas...</div>;
  }

  return (
    <div
      style={{
        padding: 40,
        maxWidth: 1000,
        margin: "0 auto",
      }}
    >
      <button
        type="button"
        className="z-back-pill"
        onClick={() => navigate("/dashboard")}
        style={{ marginBottom: 20 }}
      >
        ← Volver al panel central
      </button>

      <h1 style={{ marginBottom: 30 }}>Tu formación</h1>

      {systems.length === 0 && <p>No hay sistemas disponibles.</p>}

      <div style={{ display: "grid", gap: 20 }}>
        <div className="z-core-systems-grid">
          {systems.map((system) => {
            const progress = system.progress ?? 0;
            const hasAccess = isInternalUser || accessMap[system.id] !== false;

            return (
              <div
                key={system.id}
                className="z-core-system-card"
                onClick={() => {
                  if (hasAccess) {
                    navigate(`/core/system/${system.id}`);
                  }
                }}
                style={{
                  opacity: 1,
                  cursor: hasAccess ? "pointer" : "default",
                  position: "relative",
                }}
              >
                {!hasAccess && (
                  <div
                    style={{
                      position: "absolute",
                      top: 18,
                      right: 18,
                      zIndex: 2,
                      padding: "7px 12px",
                      borderRadius: 999,
                      background: "rgba(255, 255, 255, 0.88)",
                      border: "1px solid rgba(204, 163, 82, 0.36)",
                      color: "#9a732f",
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: "0.03em",
                      textTransform: "uppercase",
                      boxShadow: "0 10px 24px rgba(15, 47, 70, 0.08)",
                    }}
                  >
                    Próximamente
                  </div>
                )}

                {system.cover_url && (
                  <div className="z-core-system-cover">
                    <img
                      src={system.cover_url}
                      alt={system.title}
                      style={{
                        opacity: hasAccess ? 1 : 0.82,
                        filter: hasAccess ? "none" : "saturate(0.85)",
                      }}
                    />
                  </div>
                )}

                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: hasAccess ? "#0f2f46" : "rgba(15, 47, 70, 0.72)",
                    marginTop: 4,
                  }}
                >
                  {system.title}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "rgba(15, 47, 70, 0.56)",
                    marginTop: 6,
                  }}
                >
                  Duración estimada: {system.estimated_days} días
                </div>

                {hasAccess ? (
                  <>
                    <div style={{ marginTop: 14 }}>
                      <div
                        style={{
                          height: 5,
                          background: "rgba(15, 47, 70, 0.09)",
                          borderRadius: 999,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${progress}%`,
                            background: "#286b94",
                          }}
                        />
                      </div>

                      <div
                        style={{
                          marginTop: 7,
                          fontSize: 12,
                          color: "rgba(15, 47, 70, 0.56)",
                        }}
                      >
                        Progreso: {progress}%
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/core/system/${system.id}`);
                      }}
                      style={{
                        marginTop: 14,
                        alignSelf: "flex-start",
                        minHeight: 38,
                        padding: "0 18px",
                        borderRadius: 999,
                        border: "1px solid rgba(204, 163, 82, 0.42)",
                        background:
                          "linear-gradient(135deg, #0f2f46 0%, #173f5b 100%)",
                        color: "#f4d27a",
                        cursor: "pointer",
                        fontWeight: 800,
                        fontSize: 14,
                        boxShadow: "0 12px 26px rgba(15, 47, 70, 0.16)",
                      }}
                    >
                      Continuar
                    </button>
                  </>
                ) : (
                  <div
                    style={{
                      marginTop: 14,
                      padding: "10px 12px",
                      borderRadius: 14,
                      background: "rgba(204, 163, 82, 0.08)",
                      border: "1px solid rgba(204, 163, 82, 0.18)",
                      color: "rgba(15, 47, 70, 0.66)",
                      fontSize: 13,
                      lineHeight: 1.45,
                    }}
                  >
                    Este sistema hace parte de la formación y estará disponible próximamente.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
